export class Ease {

  private compute: (progress: number) => number;
  private constructor(compute: (progress: number) => number) {
    this.compute = compute;
  }

  static linear = new Ease(p => p);

  static cubic = new Ease(progress => {
    if (progress < 0.5) {
      return 4 * progress * progress * progress;
    } else {
      const p = 2 * progress - 2;
      return 0.5 * p * p * p + 1;
    }
  });

  eval(progress: number) {
    return this.compute(progress);
  }
}

type AnimCallback = (progress: number, dt: number) => void;

export class Anim {

  duration = 0
  private start = 0
  private time = 0

  private curve = Ease.linear;
  private updateCallback: AnimCallback;

  private constructor(duration: number, onUpdate: AnimCallback) {
    this.duration = duration;
    this.updateCallback = onUpdate;
  }

  update(dt: number) {
    if (this.time < this.start) {
      this.time += dt;
      if (this.time > this.start) {
        this.updateCallback(0, dt);
      }
    } else if (this.time <= this.start + this.duration) {
      this.updateCallback(this.computeProgress(), dt);
      this.time += dt;
      if (this.time > this.start + this.duration) {
        this.updateCallback(1, dt);
      }
    }
  }

  ease(curve: Ease) {
    this.curve = curve;
    return this;
  }

  private get end() {
    return this.start + this.duration;
  }

  get isCompleted() {
    return this.time >= this.end;
  }

  private computeProgress() {
    if (this.duration === 0) return 1;
    const sinceStart = Math.max(0, this.time - this.start);
    return this.curve.eval(Math.min(1, sinceStart / this.duration));
  }

  static sequence(...children: Anim[]): Anim {
    let duration = 0;

    for (const child of children) {
      child.start = duration;
      duration += child.duration;
    }

    return new Anim(duration, (_, dt) => {
      for (const child of children) {
        child.update(dt);
      }
    });
  }

  static parallel(...children: Anim[]): Anim {
    let duration = 0;

    for (const child of children) {
      duration = Math.max(duration, child.duration + child.start);
    }

    return new Anim(duration, (_, dt) => {
      for (const child of children) {
        child.update(dt);
      }
    });
  }

  static instant(callback: AnimCallback) {
    return new Anim(0, callback);
  }

  static linear(duration: number, callback: AnimCallback) {
    return new Anim(duration, callback);
  }

  static sleep(duration: number) {
    return new Anim(duration, () => {});
  }

  static ease(duration: number, ease: Ease, callback: AnimCallback) {
    return new Anim(duration, callback).ease(ease);
  }

  static loop(factory: () => Anim) {
    const player = new AnimPlayer(() => player.play(factory()));
    player.play(factory());
  }
}

export class AnimPlayer {
  private anim: Anim | null = null;
  private last = performance.now();

  private onComplete: (() => void) | null = null;

  constructor(onComplete: (() => void) | null = null) {
    this.onComplete = onComplete;
    this.requestFrame();
  }

  private requestFrame() {
    requestAnimationFrame((time) => this.processFrame(time));
  }

  private processFrame(time: number) {
    if (this.anim) {
      this.anim.update(performance.now() - this.last);
      if (this.anim.isCompleted) {
        this.anim = null;
        this.onComplete?.();
      }
    }
    this.last = time;
    this.requestFrame();
  }

  play(anim: Anim) {
    this.anim = anim;
  }
}

export function lerp(a: number, b: number, progress: number) {
  return a + (b - a) * progress;
}
